// ── 홈택스 엑셀 → transaction 후보 매퍼 ──
// 4가지 양식 자동 감지:
//   - 전자세금계산서 매입 목록
//   - 전자계산서 매입 목록 (면세)
//   - 사업용 신용카드 사용내역
//   - 현금영수증 매입내역
//
// 모두 .xls (HTML 또는 BIFF) 형식. SheetJS로 파싱.

// V148: 엑셀 라이브러리(xlsx, 약 400KB)는 가져오기를 누를 때만 받는다 — 첫 화면이 가벼워진다 (중국 망)
const loadXLSX = () => import('xlsx');

/**
 * 파일 → ArrayBuffer
 */
function readFileBuffer(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsArrayBuffer(file);
  });
}

/**
 * 워크북 첫 시트 → 행 배열 (각 행은 셀 배열)
 */
function sheetToRows(XLSX, workbook) {
  const sheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', blankrows: false });
}

/**
 * 양식 자동 감지 — 행 안의 키워드로 판단
 * @returns {'tax_invoice' | 'tax_invoice_exempt' | 'cash_receipt' | 'credit_card' | 'unknown'}
 */
function detectFormat(rows, fileName) {
  const allText = rows.slice(0, 15).flat().map(c => String(c || '')).join(' | ');
  if (allText.includes('카드사') && allText.includes('가맹점사업자번호') && allText.includes('공제여부결정')) {
    return 'credit_card';
  }
  if (allText.includes('매입일시') && allText.includes('가맹점사업자번호') && allText.includes('승인번호')) {
    return 'cash_receipt';
  }
  if (allText.includes('매입 전자(수정) 세금계산서')) {
    return 'tax_invoice';
  }
  if (allText.includes('매입 전자(수정) 계산서')) {
    return 'tax_invoice_exempt';
  }
  // 파일명 기반 폴백
  const n = (fileName || '').toLowerCase();
  if (n.includes('전자세금계산서')) return 'tax_invoice';
  if (n.includes('전자계산서')) return 'tax_invoice_exempt';
  if (n.includes('신용카드')) return 'credit_card';
  if (n.includes('매입내역')) return 'cash_receipt';
  return 'unknown';
}

/**
 * 헤더 행 인덱스 자동 탐지
 */
function findHeaderRow(rows, requiredKeyword) {
  for (let i = 0; i < Math.min(15, rows.length); i++) {
    const text = rows[i].map(c => String(c || '')).join(' ');
    if (text.includes(requiredKeyword)) return i;
  }
  return -1;
}

/**
 * 컬럼 인덱스 찾기 (헤더 행의 셀 중에서)
 */
function findCol(headerRow, ...keywords) {
  for (let i = 0; i < headerRow.length; i++) {
    const v = String(headerRow[i] || '').replace(/\s/g, '');
    for (const kw of keywords) {
      const k = kw.replace(/\s/g, '');
      if (v === k || v.includes(k)) return i;
    }
  }
  return -1;
}

/**
 * 날짜 정규화 (다양한 형식 → YYYY-MM-DD)
 */
function normalizeDate(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  // "2026-04-30" 또는 "2026-04-30 12:34:56"
  const m1 = s.match(/(\d{4})[-./](\d{1,2})[-./](\d{1,2})/);
  if (m1) {
    return `${m1[1]}-${String(m1[2]).padStart(2, '0')}-${String(m1[3]).padStart(2, '0')}`;
  }
  return null;
}

/**
 * 숫자 정규화 — 콤마 제거, 빈값/문자열 → 0
 */
function toNum(raw) {
  if (raw === null || raw === undefined || raw === '') return 0;
  if (typeof raw === 'number') return Math.round(raw);
  const cleaned = String(raw).replace(/[^\d.-]/g, '');
  const n = Number(cleaned);
  return isNaN(n) ? 0 : Math.round(n);
}

/**
 * 사업자번호 정규화 (10자리)
 */
function normBizNum(raw) {
  if (!raw) return null;
  const s = String(raw).replace(/[^\d]/g, '');
  return s.length === 10 ? s : null;
}

/**
 * 거래처명 기반 카테고리 자동 추정 (확장판)
 * 음식점 사업자 기준. 매칭 순서 중요 — 더 구체적인 게 먼저.
 */
function guessCategory(partnerName, partnerType, industry) {
  const text = `${partnerName || ''} ${partnerType || ''} ${industry || ''}`;

  // 1. 공과금 (전기·가스·수도) — 가장 명확하게 먼저
  if (/한국전력공사|한전|KEPCO|kepco|전력공사/.test(text)) return { major: 'operation', sub: 'utilities' };
  if (/도시가스|가스공사|한국가스공사|kogas|예스코|삼천리|코원에너지|예스가스|대성에너지|서울가스|대륜이엔에스|대림가스/.test(text)) return { major: 'operation', sub: 'utilities' };
  if (/한국수자원공사|수자원공사|상수도|수도사업소|상하수도/.test(text)) return { major: 'operation', sub: 'utilities' };

  // 2. 통신
  if (/kt|KT|케이티|올레|skt|SKT|에스케이텔레콤|sk텔레콤|lg유플러스|LG유플러스|lgu\+|유플러스|알뜰폰|cj헬로비전|티브로드|딜라이브|skbroadband/.test(text)) {
    return { major: 'operation', sub: 'communication' };
  }
  if (/통신|인터넷/.test(text) && !/식|음/.test(text)) return { major: 'operation', sub: 'communication' };

  // 3. 임대료·관리비
  if (/임대|월세|관리비|건물관리|부동산|공인중개|주택공사/.test(text)) return { major: 'operation', sub: 'rent' };

  // 4. 결제수수료·금융
  if (/네이버파이낸셜|네이버페이|naver pay|카카오페이|kakao pay|쿠팡페이|토스페이먼츠|tosspayments|페이먼츠|kcp|이니시스|inicis|kgmobilians|모빌리언스|결제대행|pg/i.test(text)) {
    return { major: 'operation', sub: 'fees' };
  }
  if (/배달의민족|배민|요기요|쿠팡이츠|땡겨요|위메프오/.test(text)) return { major: 'operation', sub: 'fees' };
  if (/은행|bank|새마을금고|신협|우체국|증권|투자/i.test(text)) return { major: 'operation', sub: 'fees' };

  // 5. 보험
  if (/삼성화재|현대해상|kb손해보험|메리츠화재|동부화재|한화손해|롯데손해|MG손해|AIG손해|보험/.test(text)) {
    return { major: 'operation', sub: 'taxes_dues' };
  }

  // 6. 차량·주유
  if (/주유|sk에너지|gs칼텍스|s-oil|에쓰오일|현대오일뱅크|hd오일뱅크|주유소/.test(text)) return { major: 'operation', sub: 'vehicle' };
  if (/자동차정비|카센터|타이어|모비스|렌터카|렌탈/.test(text)) return { major: 'operation', sub: 'vehicle' };

  // 7. 약국·병원·동물병원
  if (/약국|병원|의원|동물병원|치과|한의원|보건소|의료원/.test(text)) {
    return { major: 'labor', sub: 'welfare' };
  }

  // 8. 인건비·복리후생
  if (/4대보험|국민연금|건강보험|고용보험|산재보험|nps|국민건강보험|근로복지공단/i.test(text)) {
    return { major: 'labor', sub: 'social_insurance' };
  }
  if (/급여|월급|임금/.test(text)) return { major: 'labor', sub: 'salary' };

  // 9. 마케팅
  if (/광고|advertis|마케팅|sns|페이스북|인스타|구글광고|네이버광고|배너|전단/i.test(text)) {
    return { major: 'marketing', sub: 'advertising' };
  }

  // 10. 식자재
  if (/식자재|식재료|푸드|food|정육|청과|수산|축산|계란|난가공|쌀|미곡|곡물|양곡/i.test(text)) {
    return { major: 'material', sub: 'raw_material' };
  }
  if (/하나로마트|이마트|롯데마트|홈플러스|코스트코|costco|트레이더스|메가마트|농협|수협|aT/.test(text)) {
    return { major: 'material', sub: 'raw_material' };
  }
  if (/슈퍼|마트|편의점|gs25|GS25|cu|CU|세븐일레븐|이마트24|미니스톱/.test(text)) {
    return { major: 'material', sub: 'raw_material' };
  }
  if (/주류|소주|맥주|와인|음료|커피|hd음료|코카콜라|롯데칠성|하이트진로/.test(text)) {
    return { major: 'material', sub: 'raw_material' };
  }

  // 11. 소모품·운영 잡비
  if (/포장|박스|비닐|랩|호일|위생|티슈|세제|청소|살균|일회용/.test(text)) {
    return { major: 'operation', sub: 'supplies' };
  }
  if (/문구|사무용|복사|프린터|토너|잉크|모니터|컴퓨터|노트북/.test(text)) {
    return { major: 'operation', sub: 'supplies' };
  }

  // 12. 도서·교육
  if (/서점|도서|학원|강의|교육|세미나|컨퍼런스/.test(text)) return { major: 'etc', sub: 'training' };

  return { major: 'material', sub: 'raw_material' };
}

/**
 * 양식별 행 → transaction 후보 변환
 */
function parseRows(rows, format) {
  const out = [];

  if (format === 'credit_card') {
    // 헤더 행: 첫 행에 "승인일자"
    const headerIdx = findHeaderRow(rows, '승인일자');
    if (headerIdx < 0) return out;
    const h = rows[headerIdx];
    const cDate = findCol(h, '승인일자');
    const cCardCo = findCol(h, '카드사');
    const cCardNo = findCol(h, '카드번호');
    const cBizNum = findCol(h, '가맹점사업자번호');
    const cPartner = findCol(h, '가맹점명');
    const cSupply = findCol(h, '공급가액');
    const cVat = findCol(h, '세액');
    const cTaxFree = findCol(h, '비과세');
    const cTotal = findCol(h, '합계');
    const cPartnerType = findCol(h, '가맹점유형');
    const cIndustry = findCol(h, '업종');
    const cDeductible = findCol(h, '공제여부결정', '공제여부');
    const cNote = findCol(h, '비고');

    for (let i = headerIdx + 1; i < rows.length; i++) {
      const r = rows[i];
      const date = normalizeDate(r[cDate]);
      if (!date) continue;
      const partnerName = String(r[cPartner] || '').trim();
      if (!partnerName) continue;
      const deductible = String(r[cDeductible] || '').trim();
      const isDeductible = deductible === '공제';
      out.push({
        date,
        partnerName,
        partnerBizNumber: normBizNum(r[cBizNum]),
        supply: toNum(r[cSupply]),
        vat: toNum(r[cVat]),
        taxFreeAmount: toNum(r[cTaxFree]),
        total: toNum(r[cTotal]),
        evidenceType: 'card',
        isDeductible,
        deductibleNote: String(r[cNote] || '').trim() || null,
        category: guessCategory(partnerName, r[cPartnerType], r[cIndustry]),
        sourceFormat: 'credit_card',
        sourceMeta: { cardCo: String(r[cCardCo] || ''), cardNo: String(r[cCardNo] || '') },
      });
    }
    return out;
  }

  if (format === 'cash_receipt') {
    const headerIdx = findHeaderRow(rows, '매입일시');
    if (headerIdx < 0) return out;
    const h = rows[headerIdx];
    const cDate = findCol(h, '매입일시');
    const cBizNum = findCol(h, '가맹점사업자번호');
    const cPartner = findCol(h, '가맹점명');
    const cIndustry = findCol(h, '업종');
    const cPartnerType = findCol(h, '업태');
    const cSupply = findCol(h, '공급가액');
    const cVat = findCol(h, '부가세');
    const cTotal = findCol(h, '매입금액');
    const cApproval = findCol(h, '승인번호');
    const cDeductible = findCol(h, '공제여부');

    for (let i = headerIdx + 1; i < rows.length; i++) {
      const r = rows[i];
      const date = normalizeDate(r[cDate]);
      if (!date) continue;
      const partnerName = String(r[cPartner] || '').trim();
      if (!partnerName) continue;
      const deductible = String(r[cDeductible] || '').trim();
      out.push({
        date,
        partnerName,
        partnerBizNumber: normBizNum(r[cBizNum]),
        supply: toNum(r[cSupply]),
        vat: toNum(r[cVat]),
        taxFreeAmount: 0,
        total: toNum(r[cTotal]),
        evidenceType: 'cash_receipt',
        isDeductible: deductible === '공제',
        deductibleNote: null,
        category: guessCategory(partnerName, r[cPartnerType], r[cIndustry]),
        sourceFormat: 'cash_receipt',
        sourceMeta: { approvalNo: String(r[cApproval] || '') },
      });
    }
    return out;
  }

  if (format === 'tax_invoice' || format === 'tax_invoice_exempt') {
    const headerIdx = findHeaderRow(rows, '작성일자');
    if (headerIdx < 0) return out;
    const h = rows[headerIdx];
    const cDate = findCol(h, '작성일자');
    // 공급자(매입처) 정보 — 공급자사업자등록번호 다음의 상호
    const cBizNum = findCol(h, '공급자사업자등록번호');
    // "상호" 컬럼은 2개 (공급자/공급받는자) — 공급자 상호는 공급자등록번호 다음
    // findCol은 첫 번째만 찾으니까 별도 처리
    let cPartner = -1;
    for (let i = cBizNum + 1; i < h.length; i++) {
      if (String(h[i] || '').replace(/\s/g, '') === '상호') { cPartner = i; break; }
    }
    const cTotal = findCol(h, '합계금액');
    const cSupply = findCol(h, '공급가액');
    const cVat = findCol(h, '세액');
    const cApproval = findCol(h, '승인번호');
    const cItemName = findCol(h, '품목명');

    for (let i = headerIdx + 1; i < rows.length; i++) {
      const r = rows[i];
      const date = normalizeDate(r[cDate]);
      if (!date) continue;
      const partnerName = String(r[cPartner] || '').trim();
      if (!partnerName) continue;
      const supply = toNum(r[cSupply]);
      const vat = format === 'tax_invoice_exempt' ? 0 : toNum(r[cVat]);
      const total = toNum(r[cTotal]) || (supply + vat);
      out.push({
        date,
        partnerName,
        partnerBizNumber: normBizNum(r[cBizNum]),
        supply,
        vat,
        taxFreeAmount: format === 'tax_invoice_exempt' ? supply : 0,
        total,
        evidenceType: 'tax_invoice',
        isDeductible: true,  // 전자세금계산서·계산서는 적격증빙
        deductibleNote: null,
        itemName: cItemName >= 0 ? String(r[cItemName] || '').trim() : '',
        category: guessCategory(partnerName, '', ''),
        sourceFormat: format,
        sourceMeta: { approvalNo: String(r[cApproval] || '') },
      });
    }
    return out;
  }

  return out;
}

/**
 * 메인 진입점: File → { format, candidates }
 */
export async function parseHomeTaxExcel(file) {
  const buf = await readFileBuffer(file);
  const XLSX = await loadXLSX();
  const wb = XLSX.read(buf, { type: 'array' });
  const rows = sheetToRows(XLSX, wb);
  const format = detectFormat(rows, file.name);
  const candidates = parseRows(rows, format);
  return { format, candidates, fileName: file.name };
}

export const FORMAT_LABEL = {
  credit_card: '💳 사업용 신용카드',
  cash_receipt: '🧾 현금영수증',
  tax_invoice: '📋 전자세금계산서',
  tax_invoice_exempt: '🌱 전자계산서 (면세)',
  unknown: '❓ 미인식',
};

/**
 * 중복 감지 — 사업자번호 + 날짜 + 합계금액 매칭
 */
export function isDuplicate(candidate, existingTxns) {
  if (!candidate.partnerBizNumber || !candidate.date) return false;
  const cTotal = (Number(candidate.supply) || 0) + (Number(candidate.vat) || 0);
  return existingTxns.some(t => {
    if (t.type !== 'expense') return false;
    if (t.date !== candidate.date) return false;
    const tBiz = normBizNum(t.partnerBizNumber);
    if (tBiz !== candidate.partnerBizNumber) return false;
    const tTotal = (Number(t.supply) || 0) + (Number(t.vat) || 0);
    return Math.abs(tTotal - cTotal) < 100;  // ±100원 이내면 중복으로 간주
  });
}

/**
 * candidate → transaction (저장 직전 변환)
 */
export function candidateToTransaction(c) {
  const isQualified = ['card', 'cash_receipt', 'tax_invoice'].includes(c.evidenceType) && c.isDeductible;
  return {
    type: 'expense',
    date: c.date,
    partnerName: c.partnerName,
    partnerBizNumber: c.partnerBizNumber || null,
    itemName: c.itemName || '',
    supply: c.supply,
    vat: c.isDeductible ? c.vat : 0,  // 불공제면 vat=0 (종소세 비용만)
    taxFreeAmount: c.taxFreeAmount || 0,
    total: c.total,
    evidenceType: c.evidenceType,
    evidence: {
      type: c.evidenceType,
      isQualified,
      sourceUrl: null,
      externalId: c.sourceMeta?.approvalNo || null,
    },
    category: c.category,
    source: 'hometax_excel',
    needsReview: !c.isDeductible,  // 불공제면 검토 필요
    memo: buildMemo(c),
  };
}

function buildMemo(c) {
  const parts = [`[홈택스 ${FORMAT_LABEL[c.sourceFormat]?.replace(/^[^\s]+ /, '') || ''}]`];
  if (c.partnerName) parts.push(c.partnerName);
  if (c.sourceMeta?.cardCo) parts.push(c.sourceMeta.cardCo);
  if (c.sourceMeta?.cardNo) parts.push(`카드 ${c.sourceMeta.cardNo}`);
  if (c.sourceMeta?.approvalNo) parts.push(`승인 ${c.sourceMeta.approvalNo}`);
  if (!c.isDeductible) parts.push('⚠ 불공제 (홈택스 분류)');
  return parts.join(' / ');
}
